import { motion } from "framer-motion";
import { Phone, Check, Clock, ShieldCheck, Star } from "lucide-react";
import { COMPANY, HERO_IMAGE, TRUST_POINTS } from "../lib/data";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  return (
    <section data-testid="hero" className="relative overflow-hidden bg-white pt-28 md:pt-32 pb-16 md:pb-24">
      {/* soft background accents */}
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 right-0 h-80 w-80 rounded-full bg-brand-green/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div>
          <motion.span
            variants={fadeUp}
            initial="hidden"
            animate="show"
            className="chip"
          >
            <span className="h-2 w-2 rounded-full bg-brand-green animate-pulse" />
            {COMPANY.zone} — Disponible maintenant
          </motion.span>

          <motion.h1
            variants={fadeUp}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-6 font-display font-extrabold text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
          >
            Un souci ? Un artisan
            <br />
            chez vous en <span className="gradient-text">30 minutes.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            initial="hidden"
            animate="show"
            className="mt-6 text-lg text-ink-soft max-w-xl leading-relaxed"
          >
            Plomberie, serrurerie, électricité. {COMPANY.name} intervient en urgence 24h/24 et
            7j/7 à Paris et en Île-de-France. Devis clair, aucune surprise.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate="show"
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href={COMPANY.phoneHref} data-testid="hero-call-btn" className="btn-primary">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
            <a href="#contact" data-testid="hero-quote-btn" className="btn-outline">
              Demander un devis gratuit
            </a>
          </motion.div>

          <motion.ul
            variants={fadeUp}
            custom={4}
            initial="hidden"
            animate="show"
            className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3"
          >
            {TRUST_POINTS.map((t) => (
              <li key={t} className="flex items-center gap-2.5 text-ink-soft font-medium">
                <span className="h-5 w-5 rounded-full bg-brand-green/15 flex items-center justify-center">
                  <Check className="h-3 w-3 text-brand-green" />
                </span>
                {t}
              </li>
            ))}
          </motion.ul>
        </div>

        {/* Right — image with floating cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          className="relative"
        >
          <div className="relative rounded-[1.75rem] overflow-hidden shadow-2xl shadow-slate-300/50 border border-slate-200 aspect-[3/2]">
            <img src={HERO_IMAGE} alt="Artisan Le Bon Dépanneur en intervention" fetchPriority="high" decoding="async" className="h-full w-full object-cover" />
          </div>

          {/* Floating card: rating */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            className="absolute -top-4 -left-3 sm:left-6 bg-white rounded-2xl shadow-xl shadow-slate-300/40 border border-slate-100 px-4 py-3"
          >
            <div className="flex items-center gap-1 text-amber-400">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-amber-400" />
              ))}
            </div>
            <p className="text-xs text-ink-soft mt-1">
              <span className="font-bold text-ink">4.9/5</span> · 12 000+ interventions
            </p>
          </motion.div>

          {/* Floating card: 30 min */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.6 }}
            className="absolute top-1/3 -right-3 sm:right-[-1.5rem] bg-white rounded-2xl shadow-xl shadow-slate-300/40 border border-slate-100 px-4 py-3 flex items-center gap-3"
          >
            <span className="h-10 w-10 rounded-xl bg-brand/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-brand" />
            </span>
            <div>
              <p className="font-bold text-ink leading-none">30 min</p>
              <p className="text-xs text-ink-soft mt-1">Délai moyen</p>
            </div>
          </motion.div>

          {/* Floating card: guarantee */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            className="absolute -bottom-5 left-4 sm:left-10 bg-white rounded-2xl shadow-xl shadow-slate-300/40 border border-slate-100 px-4 py-3 flex items-center gap-3"
          >
            <span className="h-10 w-10 rounded-xl bg-brand-green/10 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-brand-green" />
            </span>
            <div>
              <p className="font-bold text-ink leading-none">Devis gratuit</p>
              <p className="text-xs text-ink-soft mt-1">Prix garanti sans surprise</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
